import { FieldInput } from "@/components/form/field-input";
import { FieldSelect } from "@/components/form/field-select";
import { FormContainer } from "@/components/form/form-container";
import { Badge } from "@/components/ui/badge";
import { DefaultButton, SubmitButton } from "@/components/ui/button";
import { VerticalGaps } from "@/components/ui/container";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { InputWithLabel } from "@/components/ui/input-with-label";
import { H4, P } from "@/components/ui/typography";
import { cn } from "@/utils/helper";
import { zodResolver } from "@hookform/resolvers/zod";
import { PencilIcon, TrashIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import { z } from "zod";
import { useReasonHomepass } from "@/api/master-param.ts";
import {
  ReasonHomepassItemResponse,
  useDeleteHomepassAdditional,
  useDeleteHomepassInvalid,
  useRFSHomepassAdditionalCreate,
  useRFSHomepassInvalidCreate,
} from "@/api/rfs.ts";
import { swalErrorDefault, swalSuccess } from "@/utils/lib/swal.ts";
import useHomepassData from "./hooks/useHomepassData";

function parseQtyToNumber(qty: unknown): number {
  if (typeof qty === "number") return qty;
  if (typeof qty === "string" && /^\d+$/.test(qty)) return Number(qty);
  return 0;
}

export function ContainerPrimary({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex break-inside-avoid-column flex-wrap items-center gap-x-2 border bg-white p-2 dark:bg-neutral-800",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ContainerSecondary({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(`flex w-56 items-center gap-x-2`, className)}>
      {children}
    </div>
  );
}

export function Validated() {
  return (
    <div className="flex-grow">
      <Badge variant="success">Validated</Badge>
    </div>
  );
}

export function Unvalidated() {
  return (
    <div className="flex-grow">
      <Badge variant="destructive">Unvalidated</Badge>
    </div>
  );
}

export function Failed() {
  return (
    <div className="flex-grow">
      <Badge variant="destructive">Failed</Badge>
    </div>
  );
}

export function Checking() {
  return (
    <P className="flex-grow text-axiata-blue dark:text-neutral-300">
      Checking...
    </P>
  );
}

export function Validating() {
  return (
    <div className="flex-grow">
      <Badge variant="warning">Validating</Badge>
    </div>
  );
}

export function FieldHomepassInvalid({
  hpApd,
  hpRfs,
  apdId,
  isDisabled = false,
}: {
  hpApd?: number;
  hpRfs?: number;
  apdId?: string;
  isDisabled?: boolean;
}) {
  return (
    <div className="flex items-end justify-start space-x-2">
      <div className="flex">
        <FieldInput
          name="homepassInvalid"
          label="Total Homepass Invalid"
          type="number"
          isDisabled={!isDisabled}
        />
        <div className="m-3 mb-0">
          <p style={{ color: "transparent" }}>.</p>
          <ModalSubmitReasonHomepass
            title="Homepass Invalid"
            hpApd={hpApd}
            hpRfs={hpRfs}
            apdId={apdId}
            isDisabled={isDisabled}
          />
        </div>
      </div>
    </div>
  );
}
export function FieldHomepassAdditional({
  hpApd,
  hpRfs,
  apdId,
  isDisabled = false,
}: {
  hpApd?: number;
  hpRfs?: number;
  apdId?: string;
  isDisabled?: boolean;
}) {
  return (
    <div className="flex items-end justify-start space-x-2">
      <div className="flex">
        <FieldInput
          name="homepassAdditional"
          label="Total Homepass Additional"
          type="number"
          isDisabled={!isDisabled}
        />
        <div className="m-3 mb-0">
          <p style={{ color: "transparent" }}>.</p>
          <ModalSubmitReasonHomepass
            title="Homepass Additional"
            hpApd={hpApd}
            hpRfs={hpRfs}
            apdId={apdId}
            isDisabled={isDisabled}
          />
        </div>
      </div>
    </div>
  );
}

function ModalSubmitReasonHomepass({
  title,
  hpApd,
  hpRfs,
  apdId,
  isDisabled = false,
}: {
  title: string;
  hpApd?: number;
  hpRfs?: number;
  apdId?: string;
  isDisabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const isHomepassInvalid = title === "Homepass Invalid";
  const apdUuid = apdId;
  const gapHp = (hpRfs ?? 0) - (hpApd ?? 0);

  const {
    invalidsRef: homepassInvalidData,
    additionalsRef: homepassAdditionalData,
  } = useHomepassData({
    apdId: apdUuid ?? "",
    gapHp: String(gapHp),
    isInvalid: isHomepassInvalid,
    open,
  });

  const { mutate: deleteHomepassInvalid } = useDeleteHomepassInvalid();
  const { mutate: deleteHomepassAdditional } = useDeleteHomepassAdditional();

  const homepassData = isHomepassInvalid
    ? homepassInvalidData
    : homepassAdditionalData;

  const formParent = useFormContext();
  const clusterId = formParent.getValues("clusterId");

  const defaultValues: ReasonHomepassItem[] = useMemo(() => {
    if (!homepassData || !Array.isArray(homepassData)) {
      return [];
    }

    return homepassData.map((item: ReasonHomepassItemResponse) => {
      return {
        id: item.id,
        reason:
          typeof item.reasonHomepass === "string" ? item.reasonHomepass : "",
        qty: parseQtyToNumber(item.qty),
      };
    });
  }, [homepassData]);

  const form = useForm<ModalReasonHomepassForm>({
    resolver: zodResolver(modalReasonHomepassSchema),
    defaultValues: { reasonHomepassList: [] },
  });

  useEffect(() => {
    if (open && defaultValues.length > 0) {
      form.reset({ reasonHomepassList: defaultValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, open]);

  // existing useFieldArray
  const { fields, remove, append } = useFieldArray({
    control: form.control,
    name: "reasonHomepassList",
    keyName: "reasonId",
  });

  const reason = form.watch("reason");
  const qty = form.watch("qty");

  const totalQty = fields.reduce((acc, curr) => acc + Number(curr.qty), 0);
  const isQtyEqual = totalQty === Math.abs(gapHp);

  function addReasonHomepass() {
    const values = form.getValues();
    append({ reason: values.reason ?? "", qty: values.qty ?? 0 });
    form.resetField("reason");
    form.resetField("qty");
  }

  const { data: reasonHomepass } = useReasonHomepass({
    paramType: isHomepassInvalid
      ? "ITV_REASON_INVALID"
      : "ITV_REASON_ADDITIONAL",
  });
  const reasonHomepassOptions = reasonHomepass?.content?.map(
    (reasonHomepass) => ({
      value: reasonHomepass.value,
      label: reasonHomepass.value,
    }),
  );

  const rfsInvalidMutation = useRFSHomepassInvalidCreate();
  const rfsAdditionalMutation = useRFSHomepassAdditionalCreate();
  const chosenMutation = isHomepassInvalid
    ? rfsInvalidMutation
    : rfsAdditionalMutation;
  const chosenDeleteMutation = isHomepassInvalid
    ? deleteHomepassInvalid
    : deleteHomepassAdditional;
  const { mutate: createRfsHomepass, isPending } = chosenMutation;

  function handleSubmit(values: ModalReasonHomepassForm) {
    const reasonList = values.reasonHomepassList.filter((f) => !f.id); // only new items
    const payload = reasonList.map((f) => ({
      reasonHomepass: String(f.reason ?? ""),
      qty: Number(f.qty ?? 0),
      trackerdataApd: { id: apdUuid ?? "" },
    }));

    createRfsHomepass(payload, {
      onSuccess: () => {
        setOpen(false);
        form.reset();
        if (title == "Homepass Invalid") {
          formParent.setValue("homepassInvalid", totalQty);
        } else {
          formParent.setValue("homepassAdditional", totalQty);
        }
        swalSuccess.fire({
          title: "Submission Successful",
          text: `${title} reason successfully updated`,
        });
      },
      // FIX: avoid `any` by using unknown and narrow safely
      onError: (err: unknown) => {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ??
          (err instanceof Error ? err.message : undefined) ??
          "An error occurred";
        // preserve existing behavior (alert or other UI) — use alert for parity with tests
        alert(message);
      },
    });
  }

  function getHomepassMessage(data: unknown): string | undefined {
    if (!data) return undefined;
    if (typeof data === "string") return data;
    if (Array.isArray(data)) return undefined;
    if (typeof data === "object" && data !== null) {
      const obj = data as Record<string, unknown>;
      const message =
        obj.message ??
        (obj.data && (obj.data as Record<string, unknown>).message);
      return typeof message === "string" ? message : undefined;
    }
    return undefined;
  }

  function handleRemoveReason(field: ReasonHomepassItem, index: number) {
    if (field.id) {
      chosenDeleteMutation(field.id, {
        onSuccess: () => {
          remove(index);
        },
        onError: (err: unknown) => {
          setOpen(false);
          const message =
            (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message ??
            (err instanceof Error ? err.message : undefined) ??
            "An error occurred";

          swalErrorDefault.fire(message).then(() => {
            setOpen(true);
          });
        },
      });
    } else {
      remove(index);
    }
  }

  return (
    <Dialog open={open}>
      <DialogTrigger asChild>
        <DefaultButton
          disabled={!isDisabled}
          type="button"
          mood="axiata-blue-outline"
          onClick={() => {
            setOpen(true);
          }}
        >
          <PencilIcon size={16} />
        </DefaultButton>
      </DialogTrigger>
      <DialogContent className="max-w-[800px] overflow-hidden" hideCloseButton>
        <DialogHeader>
          <DialogTitle>Update Reason {title}</DialogTitle>
          <div className="flex items-center justify-between">
            <H4 className="mb-4">Cluster ID: {clusterId}</H4>
            <H4>Gap HP: {gapHp}</H4>
          </div>
        </DialogHeader>

        <FormContainer form={form}>
          <VerticalGaps>
            <div className="grid grid-cols-3 items-end gap-x-4">
              <FieldSelect
                name="reason"
                label="Reason Homepass"
                options={reasonHomepassOptions ?? []}
              />
              <FieldInput
                name="qty"
                label={`Quantity ${title}`}
                type="number"
              />
              <DefaultButton
                mood="axiata-blue-outline"
                onClick={addReasonHomepass}
                disabled={!reason || !qty}
              >
                Add
              </DefaultButton>
            </div>

            <div className="max-h-[50vh] overflow-auto">
              <table className="w-full table-auto">
                <thead className="bg-slate-100">
                  <tr className="border-b text-left text-sm">
                    <th className="whitespace-nowrap p-4 font-medium">
                      Reason Homepass
                    </th>
                    <th className="whitespace-nowrap p-4 font-medium">
                      Qty Homepass
                    </th>
                    <th className="whitespace-nowrap p-4 font-medium">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => (
                    <tr
                      key={field.reasonId}
                      className="w-full border-b text-sm"
                    >
                      <td className="whitespace-nowrap p-4">{field.reason}</td>
                      <td className="whitespace-nowrap p-4">{field.qty}</td>
                      <td className="whitespace-nowrap p-4">
                        <DefaultButton
                          type="button"
                          mood="axiata-red"
                          onClick={() => handleRemoveReason(field, index)}
                        >
                          <TrashIcon size={16} />
                        </DefaultButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-3">
              <InputWithLabel
                label={`Total ${title}`}
                name="totalQty"
                value={totalQty}
                isDisabled
              />
            </div>
            <span className="text-sm font-medium text-red-500 dark:text-red-900">
              {getHomepassMessage(homepassData)}
            </span>
          </VerticalGaps>
        </FormContainer>
        <DialogFooter>
          <div className="flex items-center justify-end gap-x-2">
            <DefaultButton
              mood="axiata-blue-outline"
              type="button"
              onClick={() => setOpen(false)}
            >
              Cancel
            </DefaultButton>
            <SubmitButton
              type="button"
              loading={isPending}
              disabled={!isQtyEqual}
              onClick={() => void form.handleSubmit(handleSubmit)()}
            />
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const reasonHomepassItemSchema = z.object({
  id: z.string().optional(),
  reason: z.string(),
  qty: z
    .union([z.string().regex(/^\d+$/), z.number()])
    .pipe(z.coerce.number().min(1)),
});
export type ReasonHomepassItem = z.infer<typeof reasonHomepassItemSchema>;

const modalReasonHomepassSchema = z.object({
  reason: z.string().optional(),
  qty: z
    .union([z.string().regex(/^\d+$/), z.number()])
    .pipe(z.coerce.number())
    .optional(),
  reasonHomepassList: z.array(reasonHomepassItemSchema),
});
type ModalReasonHomepassForm = z.infer<typeof modalReasonHomepassSchema>;
